import type { FmpInsiderTradeRow, FmpInsiderTradingFileResponse } from '@/services/api';
import {
  insiderTransactionCode,
  insiderTransactionTypeLabel,
  tradeMatchesEnabledTypeCodes,
  type InsiderTransactionCode,
} from '@/utils/insiderTradingTransactionTypes';

export interface InsiderTradeSearchResult {
  trade: FmpInsiderTradeRow;
  transactionDate: string;
  txnCode: InsiderTransactionCode;
  reportingName: string;
  typeLabel: string;
  metricLabel: string;
  onChart: boolean;
}

function tradeMatchesSearchQuery(trade: FmpInsiderTradeRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const name = (trade.reportingName ?? '').toLowerCase();
  if (name.includes(q)) return true;
  const owner = (trade.typeOfOwner ?? '').toLowerCase();
  if (owner.includes(q)) return true;
  const security = (trade.securityName ?? '').toLowerCase();
  if (security.includes(q)) return true;
  const txn = (trade.transactionType ?? '').toLowerCase();
  if (txn.includes(q)) return true;
  const cikDigits = (trade.reportingCik ?? '').replace(/\D/g, '');
  const qDigits = q.replace(/\D/g, '');
  if (qDigits.length >= 4 && cikDigits.includes(qDigits)) return true;
  return false;
}

function formatSharesMetric(trade: FmpInsiderTradeRow): string {
  const shares = Number(trade.securitiesTransacted);
  if (!Number.isFinite(shares) || shares <= 0) return '—';
  const price = Number(trade.price);
  if (Number.isFinite(price) && price > 0) {
    const notional = shares * price;
    if (notional >= 1_000_000) return `$${(notional / 1_000_000).toFixed(1)}M`;
    if (notional >= 1_000) return `$${(notional / 1_000).toFixed(0)}K`;
    return `$${notional.toFixed(0)}`;
  }
  if (shares >= 1_000_000) return `${(shares / 1_000_000).toFixed(1)}M sh`;
  if (shares >= 1_000) return `${(shares / 1_000).toFixed(0)}K sh`;
  return `${shares} sh`;
}

export function tradeMatchesInsiderLayerFilters(
  trade: FmpInsiderTradeRow,
  enabledCodes: ReadonlySet<InsiderTransactionCode>,
  searchQuery: string
): boolean {
  if (!tradeMatchesEnabledTypeCodes(trade, enabledCodes)) return false;
  const q = searchQuery.trim();
  if (q.length >= 2 && !tradeMatchesSearchQuery(trade, q)) return false;
  return true;
}

/**
 * Search insider filings; respects type filter and optional chart-date scope for onChart flag.
 */
export function searchInsiderTrades(
  payload: FmpInsiderTradingFileResponse | null | undefined,
  query: string,
  enabledCodes: ReadonlySet<InsiderTransactionCode>,
  chartDates: ReadonlySet<string>,
  maxResults = 48
): InsiderTradeSearchResult[] {
  const q = query.trim();
  if (q.length < 2 || !payload?.trades?.length) return [];

  const out: InsiderTradeSearchResult[] = [];
  for (const trade of payload.trades) {
    if (!tradeMatchesSearchQuery(trade, q)) continue;
    if (!tradeMatchesEnabledTypeCodes(trade, enabledCodes)) continue;
    const transactionDate = (trade.transactionDate ?? '').trim();
    if (!transactionDate) continue;
    const txnCode = insiderTransactionCode(trade);
    out.push({
      trade,
      transactionDate,
      txnCode,
      reportingName: (trade.reportingName ?? 'Unknown').trim(),
      typeLabel: insiderTransactionTypeLabel(trade),
      metricLabel: formatSharesMetric(trade),
      onChart: chartDates.has(transactionDate),
    });
    if (out.length >= maxResults) break;
  }

  out.sort((a, b) => {
    const dateCmp = b.transactionDate.localeCompare(a.transactionDate);
    if (dateCmp !== 0) return dateCmp;
    return a.reportingName.localeCompare(b.reportingName);
  });
  return out;
}
